import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import Btn from "../../ui/Btn";
import NumericPagination from "../NumericPagination";
import "../../common/game.css";

const CompetitionsList = ({role = "MGR"}) => {
  const nav = useNavigate();

  useEffect(() => {
    clubStore.tournament.getCompetitions();
  }, []);

  const basePath = role === "TRN" ? "/trainer" : "/manager";
  const competitions = clubStore.tournament.competitions || [];

  const onOpenDetails = (item) => nav(`${basePath}/tournaments/show/${item.id}`);
  const onEditCompetition = (id) => nav(`${basePath}/tournament/edit/${id}`);
  const onShowCompanies = (id) => nav(`${basePath}/tournament/companies/${id}`);
  const onShowTeams = (id) => nav(`${basePath}/tournament/teams/${id}`);
  const onShowGroups = (id) => nav(`${basePath}/tournament/groups/${id}`);
  const onCreateTeam = (trn) => {
    clubStore.tournament.resetGameTeam();
    clubStore.tournament.gameTeam.setProperty("tournament_id", trn.id);
    nav(`${basePath}/tournament/team/create`);
  };
  const onEditTeam = (id) => nav(`${basePath}/tournament/team/edit/${id}`);

  const getInviteBadge = (item) => {
    if (!item.is_invited) return null;
    if (item.invite_status === "accepted") return {className: "badge badge-success", text: appStore.tr.accepted || "Accepted"};
    if (item.invite_status === "deleted" || item.invite_status === "declined") return {className: "badge badge-danger", text: appStore.tr.declined || "Declined"};
    return {className: "badge badge-warning", text: appStore.tr.invited || "Invited"};
  };

  const CompetitionCard = ({item}) => {
    const teams = item.teams || [];
    const teamsCount = Array.isArray(item.teams) ? item.teams.length : 0;
    const gamesCount = item.games_count || (Array.isArray(item.games) ? item.games.length : 0);
    const isInvited = !!item.is_invited;
    const inviteBadge = getInviteBadge(item);

    const stats = [
      {icon: "pi pi-building", value: item.companies_count ?? 0, label: appStore.tr.clubs || "Clubs"},
      {icon: "pi pi-users", value: teamsCount, label: appStore.tr.game_teams || "Teams"},
      {icon: "pi pi-calendar", value: gamesCount, label: appStore.tr.games || "Games"}
    ];

    return (
      <article className={`competition-list-card ${isInvited ? "competition-list-card--invited" : "competition-list-card--own"}`}>
        <div className="competition-list-card__body" role="button" tabIndex={0}
             onClick={() => onOpenDetails(item)} onKeyDown={(e) => e.key === "Enter" && onOpenDetails(item)}>
          <div className="competition-list-card__header">
            <div className="competition-list-card__sport" aria-hidden="true">{item.sport_icon || "🏆"}</div>
            <div className="competition-list-card__heading">
              <div className="competition-list-card__title">{item.name}</div>
              <div className="competition-list-card__organizer">{appStore.tr.organizer}: {item.company?.name || "—"}</div>
            </div>
            <i className="pi pi-angle-right competition-list-card__arrow" aria-hidden="true"/>
          </div>

          {(item.start_date || item.end_date || item.address) && (
            <div className="competition-list-card__details">
              {(item.start_date || item.end_date) && (
                <div className="competition-list-card__detail"><i className="pi pi-calendar" aria-hidden="true"/>
                  <span>{item.start_date || "—"}{item.end_date && item.end_date !== item.start_date ? ` — ${item.end_date}` : ""}</span>
                </div>
              )}
              {item.address && (
                <div className="competition-list-card__detail"><i className="pi pi-map-marker" aria-hidden="true"/>
                  <span>{item.address}</span>
                </div>
              )}
            </div>
          )}

          {inviteBadge && <div className="competition-list-card__status"><span className={inviteBadge.className}>{inviteBadge.text}</span></div>}
          {isInvited && item.invite_status === "invited" && (
            <div className="competition-list-card__invitation">
              <i className="pi pi-envelope" aria-hidden="true"/>{appStore.tr.tournament_invitation_text}
            </div>
          )}

          <div className="competition-list-stats">
            {stats.map((stat, index) => (
              <div key={index} className="competition-list-stat">
                <div className="competition-list-stat__value">{stat.value}</div>
                <div className="competition-list-stat__label">
                  <i className={stat.icon} aria-hidden="true"/><span>{stat.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="competition-list-card__teams">
            <div className="competition-list-card__section-title">{appStore.tr.game_teams || "Teams"}</div>
            {teams.length ? (
              <div className="competition-list-card__chips">
                {teams.slice(0, 8).map((team) => (
                  <span key={team.id} className="competition-list-card__chip" title={team.name}>
                    <i className="pi pi-users" aria-hidden="true"/><span>{team.name}</span>
                  </span>
                ))}
                {teams.length > 8 && <span className="competition-list-card__chip">+{teams.length - 8}</span>}
              </div>
            ) : (
              <div className="competition-list-card__empty-teams">{appStore.tr.teams_not_selected || "Teams Not Selected"}</div>
            )}
          </div>
        </div>

        <div className="competition-list-card__actions">
          {item.is_owner && role === "MGR" && (
            <>
              <Btn icon="pi pi-pencil" tooltip={appStore.tr.edit} onClick={() => onEditCompetition(item.id)}/>
              <Btn icon="pi pi-sitemap" severity="secondary" tooltip={appStore.tr.clubs} onClick={() => onShowCompanies(item.id)}/>
              <Btn icon="pi pi-users" severity="secondary" tooltip={appStore.tr.game_teams} onClick={() => onShowTeams(item.id)}/>
              <Btn icon="pi pi-th-large" severity="secondary" tooltip={appStore.tr.groups} onClick={() => onShowGroups(item.id)}/>
            </>
          )}
          {role === "MGR" && item.fee > 0 && item.invite_status === "accepted" && (
            <Btn icon="pi pi-dollar" severity="secondary" onClick={() => nav(`${basePath}/tournament/billing/${item.id}`)}/>
          )}
          {role === "MGR" && item.invite_status === "accepted" && (
            <Btn icon="pi pi-users" severity="secondary" tooltip={appStore.tr.game_teams} onClick={() => onShowTeams(item.id)}/>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="competition-list">
      {!competitions.length ? (
        <div className="competition-list-empty">
          <i className="pi pi-trophy" aria-hidden="true"/>
          <span>{appStore.tr.not_found}</span>
        </div>
      ) : (
        <div className="competition-list-grid">
          {competitions.map((item) => <CompetitionCard key={item.id} item={item}/>)}
        </div>
      )}
      <div className="competition-list-pagination">
        <NumericPagination query={clubStore.tournament.competitionsQuery} onPageChange={(page) => {
          clubStore.tournament.competitionsQuery.setProperty("page_number", page);
          clubStore.tournament.getCompetitions();
        }}/>
      </div>
    </div>
  );
};

export default observer(CompetitionsList);
